import { CaseForm, FileEvidences } from 'queries/cases/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadIcon } from '../UploadIcon';
import { DragDropArea } from '../DragDropArea/DragDropArea';
import './UploadFiles.scss';
import { Evidence } from '../EvidenceContent/Evidence';
import { Loading, PrimaryButton, Tooltip, useModal } from '@shape-ds/components';
import { AddCircleOutlineOutlined } from '@mui/icons-material';
import { useWatch, useFormContext, UseFormHandleSubmit } from 'react-hook-form';
import { useFileUploadMutation } from 'queries';
import { useGlobalNotification } from 'hooks';
import { handleUploadFile } from 'pages/Asset/tabs/Events/components/Records/utils';
import { useSaveEvidences } from 'components/Evidences/hooks/useSaveEvidences';

interface UploadFilesProps {
  acceptFiles?: string;
  caseId: string;
  handleSubmit: UseFormHandleSubmit<CaseForm>;
}

export const UploadFiles = ({ acceptFiles, caseId, handleSubmit }: UploadFilesProps) => {
  const { t } = useTranslation();
  const { displaySnackbar } = useGlobalNotification();
  const { setValue } = useFormContext();
  const { closeModal } = useModal();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const evidencesRaw = useWatch({ name: 'evidences' });
  const evidences = useMemo(() => evidencesRaw || [], [evidencesRaw]);
  const [isEnabled, setIsEnabled] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const {
    filesData,
    refetchListFiles,
    isLoading: isLoadingFiles,
  } = useSaveEvidences({
    eventId: caseId,
  });

  const visibleEvidences = useMemo(
    () => evidences.filter((evidence: FileEvidences) => !evidence.isDeleted),
    [evidences],
  );

  useEffect(() => {
    if (filesData?.files && Array.isArray(filesData.files) && hasInitialized) {
      const serverEvidences = filesData.files.map((fileName) => ({
        name: fileName,
        isNew: false,
        isDeleted: false,
        file: null,
      }));

      const currentEvidences = evidences || [];
      const newFiles = currentEvidences.filter(
        (evidence: FileEvidences) => evidence.isNew && evidence.file,
      );

      const combinedEvidences = [...serverEvidences, ...newFiles];

      const currentNames = currentEvidences.map((e: FileEvidences) => e.name).sort();
      const combinedNames = combinedEvidences.map((e: FileEvidences) => e.name).sort();

      if (JSON.stringify(currentNames) !== JSON.stringify(combinedNames)) {
        setValue('evidences', combinedEvidences);
      }
    }
  }, [filesData?.files, setValue, hasInitialized, evidences]);

  useEffect(() => {
    const initializeFormData = async () => {
      setIsRefetching(true);
      await refetchListFiles();
      setIsRefetching(false);

      if (filesData?.files && Array.isArray(filesData.files) && !hasInitialized) {
        const serverEvidences = filesData.files.map((fileName) => ({
          name: fileName,
          isNew: false,
          isDeleted: false,
          file: null,
        }));

        setValue('evidences', serverEvidences);
        setHasInitialized(true);
      }
    };

    initializeFormData();
  }, [setValue, hasInitialized, refetchListFiles, filesData?.files]);

  useEffect(() => {
    if (visibleEvidences.length > 0) {
      setIsEnabled(true);
    }
    if (visibleEvidences.length === 0) {
      setIsEnabled(false);
    }
  }, [visibleEvidences]);

  const onError = () => {
    displaySnackbar({
      message: t('event_page:snackbarMessages.error.message'),
      type: 'error',
    });
  };
  const fileUploadMutation = useFileUploadMutation(onError);
  const isUploading = fileUploadMutation.isLoading || isRefetching || isLoadingFiles;

  const handleFileDrop = (file: File) => {
    if (fileInputRef.current) {
      const newFile = new DataTransfer();
      newFile.items.add(file);
      fileInputRef.current.files = newFile.files;

      const addFileEvent = new Event('change', { bubbles: true });
      fileInputRef.current.dispatchEvent(addFileEvent);
    }
  };

  const handleSaveClick = async () => {
    const newEvidences = [...evidences];
    const filesToUpload = newEvidences.filter((evidence) => evidence.file);

    let uploadSuccessCount = 0;
    let uploadFailCount = 0;

    for (const evidence of filesToUpload) {
      try {
        await handleUploadFile(evidence.file, caseId, fileUploadMutation);
        uploadSuccessCount++;
      } catch {
        uploadFailCount++;
        displaySnackbar({
          message: t('case:evidences.messages.upload_failed', { fileName: evidence.name }),
          type: 'error',
        });
      }
    }

    setIsRefetching(true);
    await refetchListFiles();
    setIsRefetching(false);

    handleSubmit(() => {
      if (uploadSuccessCount > 0) {
        displaySnackbar({
          message: t('case:evidences.messages.upload_success'),
          type: 'success',
        });
      }
      if (uploadFailCount > 0) {
        displaySnackbar({
          message: t('case:evidences.messages.some_uploads_failed', { count: uploadFailCount }),
          type: 'warning',
        });
      }
    })();
    closeModal();
  };

  return (
    <div className="upload-files-container">
      <div className="evidences-wrapper" data-testid="evidence-component">
        {isRefetching || isLoadingFiles ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <Loading />
          </div>
        ) : (
          visibleEvidences?.map((file: FileEvidences, index: number) => (
            <Evidence {...file} index={index} key={file.name} handleSubmit={handleSubmit} />
          ))
        )}
      </div>

      <DragDropArea onFileDrop={handleFileDrop} className="upload-files-component">
        {isUploading ? (
          <Loading />
        ) : (
          <>
            <p className="upload-drag-drop-text">{t('case:evidences.drop_file_here')}</p>
            <Tooltip title={t('case:evidences.messages.tooltip')}>
              <PrimaryButton
                onClick={() => fileInputRef.current?.click()}
                startIcon={<AddCircleOutlineOutlined />}
                className="attach-evidence-btn"
              >
                {t('case:evidences.search_in_device')}
              </PrimaryButton>
            </Tooltip>

            <UploadIcon fileInputRef={fileInputRef} acceptFiles={acceptFiles} />
          </>
        )}
      </DragDropArea>
      <Tooltip
        title={
          isEnabled
            ? t('case:evidences.save_attched_files')
            : t('case:evidences.upload_files_to_continue')
        }
        contentClassName="add-evidence-tooltip"
      >
        <PrimaryButton
          onClick={() => handleSaveClick()}
          startIcon={<AddCircleOutlineOutlined />}
          disabled={!isEnabled}
          className={`${!isEnabled ? 'attach-btn-disabled' : ''}`}
        >
          {t('monitoring:tables.messages.addEvidence')}
        </PrimaryButton>
      </Tooltip>
    </div>
  );
};
